import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/store/shared/supabase";
import { dispatchDataError, describeSupabaseError, resolveWriteErrorMessage } from "@/store/shared/dataErrors";
import { fetchScopeIds, EMPTY_SCOPE_IDS, type ScopeIds } from "@/store/shared/scopeIds";
import { useSession } from "@/session/SessionProvider";
import { createUuid } from "@/store/shared/id";
import {
  contratStatutToDb,
  depenseCategorieToDb,
  mapContratRow,
  mapDepenseRow,
  mapLoyerRow,
  mapPaiementRow,
  modePaiementToDb,
  monthFirstDay,
  nullable as nullish,
  todayISO,
  type ImmeubleScopeInfo,
  type PlatformDepense,
  type PlatformDepenseInput,
  type PlatformLoyer,
  type PlatformPaiement,
  type PlatformPaiementInput,
} from "./bloc6";
import type {
  PlatformBien,
  PlatformBienInput,
  PlatformContrat,
  PlatformContratInput,
  PlatformImmeuble,
  PlatformImmeubleInput,
  PlatformLocataire,
  PlatformLocataireInput,
  PlatformScope,
} from "./types";

interface PlatformDataContextValue {
  immeubles: PlatformImmeuble[];
  biens: PlatformBien[];
  locataires: PlatformLocataire[];
  contrats: PlatformContrat[];
  loyers: PlatformLoyer[];
  impayes: PlatformLoyer[];
  paiements: PlatformPaiement[];
  depenses: PlatformDepense[];
  /** Chargement initial depuis Supabase. */
  loading: boolean;
  /** Erreur de lecture éventuelle (message lisible) — sinon `null`. */
  error: string | null;
  /** Recharge toutes les données depuis Supabase. */
  reload: () => Promise<void>;
  addImmeuble: (input: PlatformImmeubleInput) => PlatformImmeuble;
  updateImmeuble: (id: string, input: PlatformImmeubleInput) => void;
  removeImmeuble: (id: string) => void;
  addBien: (input: PlatformBienInput) => PlatformBien;
  updateBien: (id: string, input: PlatformBienInput) => void;
  removeBien: (id: string) => void;
  addLocataire: (input: PlatformLocataireInput) => PlatformLocataire;
  updateLocataire: (id: string, input: PlatformLocataireInput) => void;
  removeLocataire: (id: string) => void;
  addContrat: (input: PlatformContratInput) => PlatformContrat;
  updateContrat: (id: string, input: PlatformContratInput) => void;
  removeContrat: (id: string) => void;
  addPaiement: (input: PlatformPaiementInput) => PlatformPaiement;
  removePaiement: (id: string) => void;
  addDepense: (input: PlatformDepenseInput) => PlatformDepense;
  updateDepense: (id: string, input: PlatformDepenseInput) => void;
  removeDepense: (id: string) => void;
  /** Régénère les échéances des contrats actifs puis recharge les loyers. */
  syncLoyers: () => Promise<void>;
  getImmeuble: (id: string) => PlatformImmeuble | undefined;
  getBien: (id: string) => PlatformBien | undefined;
  getLocataire: (id: string) => PlatformLocataire | undefined;
  getContrat: (id: string) => PlatformContrat | undefined;
  biensByImmeuble: (immeubleId: string) => PlatformBien[];
  contratsByImmeuble: (immeubleId: string) => PlatformContrat[];
  contratsByBien: (bienId: string) => PlatformContrat[];
  contratsByLocataire: (locataireId: string) => PlatformContrat[];
  countContratsByBien: (bienId: string) => number;
  countContratsByLocataire: (locataireId: string) => number;
  contratActifByBien: (bienId: string) => PlatformContrat | undefined;
}

const PlatformDataContext = createContext<PlatformDataContextValue | null>(null);

/* ------------------------------------------------------------------ */
/* Helpers de mapping Supabase <-> modèles frontend                     */
/* ------------------------------------------------------------------ */

interface TerritoryNames {
  regions: Map<string, string>;
  provinces: Map<string, string>;
  communes: Map<string, string>;
  secteurs: Map<string, string>;
}

type Row = Record<string, unknown>;

const str = (value: unknown): string => (typeof value === "string" ? value : "");
const num = (value: unknown): number => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const nullable = (value: string): string | null => (value && value.trim() ? value : null);

/**
 * Codes d'erreur PostgREST/Postgres considérés comme TRANSITOIRES.
 * `57014` = statement timeout (souvent une attente de verrou coupée par le
 * statement_timeout, cf. lock_timeout à 0 côté Supabase). Ces erreurs peuvent
 * réussir si la requête est relancée : elles ne traduisent pas une règle métier
 * mais un aléa serveur momentané.
 */
const TRANSIENT_ERROR_CODES = new Set([
  "57014", // statement timeout
  "57P01", // admin shutdown
  "57P02", // crash shutdown
  "08000", // connection exception
  "08003",
  "08006",
  "PGRST002",
]);

function isTransientError(error: unknown): boolean {
  const code = (error as { code?: string } | null)?.code;
  return typeof code === "string" && TRANSIENT_ERROR_CODES.has(code);
}

/**
 * Exécute une requête Supabase en la relançant quelques fois en cas d'erreur
 * TRANSITOIRE uniquement. Une erreur définitive (RLS, contrainte, permission)
 * n'est jamais relancée : elle remonte immédiatement.
 */
async function fetchWithRetry<T extends { error: unknown }>(
  factory: () => PromiseLike<T>,
  attempts = 3
): Promise<T> {
  let result = await factory();
  for (let i = 1; i < attempts && result.error && isTransientError(result.error); i += 1) {
    await new Promise((resolve) => {
      setTimeout(resolve, 400 * i);
    });
    result = await factory();
  }
  return result;
}

function buildNameMap(rows: Row[] | null): Map<string, string> {
  const map = new Map<string, string>();
  (rows ?? []).forEach((row) => {
    const id = str(row.id);
    const nom = str(row.nom);
    if (id) map.set(id, nom);
  });
  return map;
}

function rowToImmeuble(row: Row, territory: TerritoryNames): PlatformImmeuble {
  const agenceId = str(row.agence_id);
  const scope: PlatformScope = agenceId ? "agence" : "proprietaire";
  const regionId = str(row.region_id);
  const provinceId = str(row.province_id);
  const communeId = str(row.commune_id);
  const secteurId = str(row.secteur_id);
  return {
    id: str(row.id),
    scope,
    proprietaireId: str(row.proprietaire_id),
    nom: str(row.nom),
    description: str(row.description),
    photo: str(row.photo),
    regionId,
    provinceId,
    communeId,
    secteurId,
    region: territory.regions.get(regionId) ?? "",
    province: territory.provinces.get(provinceId) ?? "",
    commune: territory.communes.get(communeId) ?? "",
    secteur: territory.secteurs.get(secteurId) ?? "",
    adresse: str(row.adresse),
    niveaux: Number(row.nombre_niveaux) || 1,
    infosComplementaires: str(row.infos_complementaires),
    createdAt: str(row.created_at),
  };
}

function rowToBien(row: Row, immeubleScope: Map<string, PlatformScope>, immeubleOwner: Map<string, string>): PlatformBien {
  const immeubleId = str(row.immeuble_id);
  return {
    id: str(row.id),
    scope: immeubleScope.get(immeubleId) ?? "proprietaire",
    proprietaireId: immeubleOwner.get(immeubleId) ?? "",
    immeubleId,
    type: str(row.type_bien),
    reference: str(row.reference),
    etage: str(row.etage),
    numero: str(row.numero),
    surface: num(row.surface),
    loyerMensuel: num(row.loyer_mensuel),
    // La base n'accepte que les statuts Supabase ; on n'expose jamais « hors-service ».
    statut: str(row.statut) || "disponible",
    description: str(row.description),
    infosComplementaires: str(row.infos_complementaires),
    createdAt: str(row.created_at),
  };
}

function rowToLocataire(row: Row): PlatformLocataire {
  const agenceId = str(row.agence_id);
  const proprietaireId = str(row.proprietaire_id);
  return {
    id: str(row.id),
    scope: agenceId ? "agence" : "proprietaire",
    prenom: str(row.prenom),
    nom: str(row.nom),
    telephone: str(row.telephone),
    whatsapp: str(row.whatsapp),
    email: str(row.email),
    adresse: str(row.adresse),
    profession: str(row.profession),
    typePiece: str(row.type_piece),
    numeroPiece: str(row.numero_piece),
    contactUrgence: str(row.contact_urgence),
    proprietaireId: proprietaireId || undefined,
    agenceId: agenceId || undefined,
    notes: str(row.notes),
    createdAt: str(row.created_at),
  };
}

/** Statut bien normalisé côté Supabase (jamais « hors-service »). */
function normalizeBienStatut(statut: string): string {
  return statut === "hors-service" ? "indisponible" : statut;
}

/**
 * Matérialise les échéances manquantes des contrats actifs / à venir via la RPC
 * `generer_loyers_periode`. Opération idempotente et non bloquante : les loyers
 * déjà existants ne sont pas dupliqués. Aucune donnée fictive n'est créée.
 */
async function genererLoyersManquants(contrats: PlatformContrat[]): Promise<void> {
  const currentMonthKey = todayISO().slice(0, 7);
  const currentMonthFirst = monthFirstDay(currentMonthKey);
  for (const contrat of contrats) {
    if (contrat.statut !== "actif" && contrat.statut !== "brouillon") continue;
    if (!contrat.dateDebut) continue;
    const debut = monthFirstDay(contrat.dateDebut.slice(0, 7));
    const finMonthKey = contrat.dateFin ? contrat.dateFin.slice(0, 7) : currentMonthKey;
    let fin = monthFirstDay(finMonthKey);
    if (fin > currentMonthFirst) fin = currentMonthFirst;
    if (fin < debut) continue;
    try {
      await supabase.rpc("generer_loyers_periode", {
        p_contrat_id: contrat.id,
        p_debut: debut,
        p_fin: fin,
      });
    } catch (caught) {
      console.warn("[PlatformData] génération des loyers ignorée", contrat.id, caught);
    }
  }
}

/**
 * Store unifié de la plateforme — branché sur Supabase.
 *
 * Immeubles, biens, locataires, contrats, loyers, paiements et dépenses
 * proviennent RÉELLEMENT des tables et vues Supabase (Bloc 6 inclus).
 * La RLS filtre automatiquement chaque périmètre (propriétaire, agence,
 * employé, super admin). Aucune donnée fictive, aucune persistance locale.
 */
export function PlatformDataProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const [immeubles, setImmeubles] = useState<PlatformImmeuble[]>([]);
  const [biens, setBiens] = useState<PlatformBien[]>([]);
  const [locataires, setLocataires] = useState<PlatformLocataire[]>([]);
  const [contrats, setContrats] = useState<PlatformContrat[]>([]);
  const [loyers, setLoyers] = useState<PlatformLoyer[]>([]);
  const [impayes, setImpayes] = useState<PlatformLoyer[]>([]);
  const [paiements, setPaiements] = useState<PlatformPaiement[]>([]);
  const [depenses, setDepenses] = useState<PlatformDepense[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scopeRef = useRef<ScopeIds>(EMPTY_SCOPE_IDS);
  const immeubleRattachementRef = useRef<Map<string, { proprietaire_id: string | null; agence_id: string | null }>>(
    new Map()
  );
  const locataireRattachementRef = useRef<Map<string, { proprietaire_id: string | null; agence_id: string | null }>>(
    new Map()
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      scopeRef.current = await fetchScopeIds();

      const [immeublesRes, biensRes, locatairesRes, regionsRes, provincesRes, communesRes, secteursRes] =
        await Promise.all([
          fetchWithRetry(() => supabase.from("immeubles").select("*").order("created_at", { ascending: false })),
          fetchWithRetry(() => supabase.from("biens").select("*").order("created_at", { ascending: false })),
          fetchWithRetry(() => supabase.from("locataires").select("*").order("created_at", { ascending: false })),
          fetchWithRetry(() => supabase.from("regions").select("id, nom")),
          fetchWithRetry(() => supabase.from("provinces").select("id, nom")),
          fetchWithRetry(() => supabase.from("communes").select("id, nom")),
          fetchWithRetry(() => supabase.from("secteurs").select("id, nom")),
        ]);

      if (immeublesRes.error) throw immeublesRes.error;
      if (biensRes.error) throw biensRes.error;
      if (locatairesRes.error) throw locatairesRes.error;

      const territory: TerritoryNames = {
        regions: buildNameMap(regionsRes.data as Row[] | null),
        provinces: buildNameMap(provincesRes.data as Row[] | null),
        communes: buildNameMap(communesRes.data as Row[] | null),
        secteurs: buildNameMap(secteursRes.data as Row[] | null),
      };

      const immeubleRows = (immeublesRes.data as Row[] | null) ?? [];
      const mappedImmeubles = immeubleRows.map((row) => rowToImmeuble(row, territory));

      const scopeByImmeuble = new Map<string, PlatformScope>();
      const ownerByImmeuble = new Map<string, string>();
      const rattachementImmeubles = new Map<string, { proprietaire_id: string | null; agence_id: string | null }>();
      mappedImmeubles.forEach((immeuble) => {
        scopeByImmeuble.set(immeuble.id, immeuble.scope);
        ownerByImmeuble.set(immeuble.id, immeuble.proprietaireId);
        rattachementImmeubles.set(immeuble.id, {
          proprietaire_id: immeuble.proprietaireId || null,
          agence_id: null,
        });
      });
      // Le rattachement agence_id réel est lu directement sur la ligne.
      immeubleRows.forEach((row) => {
        const id = str(row.id);
        rattachementImmeubles.set(id, {
          proprietaire_id: str(row.proprietaire_id) || null,
          agence_id: str(row.agence_id) || null,
        });
      });
      immeubleRattachementRef.current = rattachementImmeubles;

      const locataireRows = (locatairesRes.data as Row[] | null) ?? [];
      const rattachementLocataires = new Map<
        string,
        { proprietaire_id: string | null; agence_id: string | null }
      >();
      locataireRows.forEach((row) => {
        rattachementLocataires.set(str(row.id), {
          proprietaire_id: str(row.proprietaire_id) || null,
          agence_id: str(row.agence_id) || null,
        });
      });
      locataireRattachementRef.current = rattachementLocataires;

      setImmeubles(mappedImmeubles);
      setBiens(((biensRes.data as Row[] | null) ?? []).map((row) => rowToBien(row, scopeByImmeuble, ownerByImmeuble)));
      setLocataires(locataireRows.map(rowToLocataire));

      /* ----------------------- Bloc 6 (Supabase) ----------------------- */

      const immeubleInfo = new Map<string, ImmeubleScopeInfo>();
      mappedImmeubles.forEach((immeuble) =>
        immeubleInfo.set(immeuble.id, { scope: immeuble.scope, proprietaireId: immeuble.proprietaireId })
      );

      const bienToImmeuble = new Map<string, string>();
      ((biensRes.data as Row[] | null) ?? []).forEach((row) => bienToImmeuble.set(str(row.id), str(row.immeuble_id)));

      const contratsRes = await fetchWithRetry(() =>
        supabase.from("contrats").select("*").order("created_at", { ascending: false })
      );
      if (contratsRes.error) throw contratsRes.error;
      const mappedContrats = ((contratsRes.data as Row[] | null) ?? []).map((row) =>
        mapContratRow(row, bienToImmeuble, immeubleInfo)
      );

      // Matérialise les échéances manquantes AVANT de lire les loyers.
      await genererLoyersManquants(mappedContrats);

      const contratOwner = new Map<string, string>();
      mappedContrats.forEach((contrat) => contratOwner.set(contrat.id, contrat.proprietaireId));

      // Les données de base (immeubles, biens, locataires, contrats) sont déjà
      // affichables. Les agrégats loyers/impayés/paiements/dépenses sont lus
      // dans un bloc isolé : si l'un d'eux échoue (timeout transitoire), le
      // reste du tableau de bord ne doit pas être vidé.
      setContrats(mappedContrats);

      try {
        const [loyersRes, impayesRes, paiementsRes, depensesRes] = await Promise.all([
          fetchWithRetry(() => supabase.from("v_loyers_details").select("*")),
          fetchWithRetry(() => supabase.from("v_impayes").select("*")),
          fetchWithRetry(() => supabase.from("v_paiements_details").select("*").order("date_paiement", { ascending: false })),
          fetchWithRetry(() => supabase.from("depenses").select("*").order("date_depense", { ascending: false })),
        ]);
        if (loyersRes.error) throw loyersRes.error;
        if (impayesRes.error) throw impayesRes.error;
        if (paiementsRes.error) throw paiementsRes.error;
        if (depensesRes.error) throw depensesRes.error;

        setLoyers(((loyersRes.data as Row[] | null) ?? []).map((row) => mapLoyerRow(row, immeubleInfo, contratOwner)));
        setImpayes(((impayesRes.data as Row[] | null) ?? []).map((row) => mapLoyerRow(row, immeubleInfo, contratOwner)));
        setPaiements(
          ((paiementsRes.data as Row[] | null) ?? []).map((row) => mapPaiementRow(row, immeubleInfo, contratOwner))
        );
        setDepenses(
          ((depensesRes.data as Row[] | null) ?? []).map((row) => mapDepenseRow(row, immeubleInfo, bienToImmeuble))
        );
      } catch (caught) {
        const message = describeSupabaseError(caught);
        setError(message);
        console.error("[PlatformData] chargement des loyers/paiements/dépenses échoué", caught);
      }
    } catch (caught) {
      const message = describeSupabaseError(caught);
      setError(message);
      console.error("[PlatformData] chargement Supabase échoué", caught);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!session) {
      setImmeubles([]);
      setBiens([]);
      setLocataires([]);
      setContrats([]);
      setLoyers([]);
      setImpayes([]);
      setPaiements([]);
      setDepenses([]);
      return;
    }
    void load();
  }, [session, load]);

  /* ------------------------------ Immeubles ----------------------------- */

  const addImmeuble = useCallback(
    (input: PlatformImmeubleInput) => {
      const optimistic: PlatformImmeuble = { ...input, id: createUuid(), createdAt: new Date().toISOString() };
      setImmeubles((prev) => [optimistic, ...prev]);

      const scopeIds = scopeRef.current;
      const isAgence = input.scope === "agence";
      const proprietaireId = isAgence
        ? nullable(input.proprietaireId)
        : nullable(input.proprietaireId) ?? scopeIds.proprietaireId;
      const agenceId = isAgence ? scopeIds.agenceId : null;

      const payload = {
        proprietaire_id: proprietaireId,
        agence_id: agenceId,
        nom: input.nom,
        adresse: nullable(input.adresse),
        region_id: nullable(input.regionId),
        province_id: nullable(input.provinceId),
        commune_id: nullable(input.communeId),
        secteur_id: nullable(input.secteurId),
        description: nullable(input.description),
        nombre_biens: 0,
        nombre_niveaux: input.niveaux || 1,
        infos_complementaires: nullable(input.infosComplementaires),
        photo: nullable(input.photo),
      };

      void (async () => {
        const { data, error: insertError } = await supabase
          .from("immeubles")
          .insert(payload)
          .select("id")
          .maybeSingle();
        if (insertError) {
          setImmeubles((prev) => prev.filter((item) => item.id !== optimistic.id));
          dispatchDataError({
            scope: "Immeubles",
            message: resolveWriteErrorMessage(insertError, "Impossible d'enregistrer l'immeuble."),
            technical: describeSupabaseError(insertError),
          });
          return;
        }
        const realId = (data as Row | null)?.id;
        if (realId) {
          setImmeubles((prev) => prev.map((item) => (item.id === optimistic.id ? { ...item, id: String(realId) } : item)));
        }
        void load();
      })();

      return optimistic;
    },
    [load]
  );

  const updateImmeuble = useCallback(
    (id: string, input: PlatformImmeubleInput) => {
      const previous = immeubles.find((item) => item.id === id);
      setImmeubles((prev) => prev.map((item) => (item.id === id ? { ...item, ...input } : item)));

      const rattachement = immeubleRattachementRef.current.get(id);
      const payload: Record<string, unknown> = {
        nom: input.nom,
        adresse: nullable(input.adresse),
        region_id: nullable(input.regionId),
        province_id: nullable(input.provinceId),
        commune_id: nullable(input.communeId),
        secteur_id: nullable(input.secteurId),
        description: nullable(input.description),
        nombre_niveaux: input.niveaux || 1,
        infos_complementaires: nullable(input.infosComplementaires),
        photo: nullable(input.photo),
      };
      // On préserve le rattachement d'origine (jamais de changement de périmètre).
      if (rattachement) {
        payload.proprietaire_id = rattachement.proprietaire_id;
        payload.agence_id = rattachement.agence_id;
      }

      void (async () => {
        const { error: updateError } = await supabase.from("immeubles").update(payload).eq("id", id);
        if (updateError) {
          if (previous) setImmeubles((prev) => prev.map((item) => (item.id === id ? previous : item)));
          dispatchDataError({
            scope: "Immeubles",
            message: "Impossible de modifier l'immeuble.",
            technical: describeSupabaseError(updateError),
          });
          return;
        }
        void load();
      })();
    },
    [immeubles, load]
  );

  const removeImmeuble = useCallback(
    (id: string) => {
      const previous = immeubles.find((item) => item.id === id);
      setImmeubles((prev) => prev.filter((item) => item.id !== id));
      void (async () => {
        const { error: deleteError } = await supabase.from("immeubles").delete().eq("id", id);
        if (deleteError) {
          if (previous) setImmeubles((prev) => [previous, ...prev]);
          dispatchDataError({
            scope: "Immeubles",
            message: "Impossible de supprimer l'immeuble (il contient peut-être des biens ou documents rattachés).",
            technical: describeSupabaseError(deleteError),
          });
          return;
        }
        void load();
      })();
    },
    [immeubles, load]
  );

  /* -------------------------------- Biens ------------------------------- */

  const addBien = useCallback(
    (input: PlatformBienInput) => {
      const optimistic: PlatformBien = { ...input, id: createUuid(), createdAt: new Date().toISOString() };
      setBiens((prev) => [optimistic, ...prev]);

      const payload = {
        immeuble_id: input.immeubleId,
        type_bien: input.type,
        reference: nullable(input.reference),
        numero: nullable(input.numero),
        etage: nullable(input.etage),
        surface: input.surface || 0,
        loyer_mensuel: input.loyerMensuel || 0,
        statut: normalizeBienStatut(input.statut),
        description: nullable(input.description),
        infos_complementaires: nullable(input.infosComplementaires),
      };

      void (async () => {
        const { data, error: insertError } = await supabase.from("biens").insert(payload).select("id").maybeSingle();
        if (insertError) {
          setBiens((prev) => prev.filter((item) => item.id !== optimistic.id));
          dispatchDataError({
            scope: "Biens",
            message: "Impossible d'enregistrer le bien.",
            technical: describeSupabaseError(insertError),
          });
          return;
        }
        const realId = (data as Row | null)?.id;
        if (realId) {
          setBiens((prev) => prev.map((item) => (item.id === optimistic.id ? { ...item, id: String(realId) } : item)));
        }
        void load();
      })();

      return optimistic;
    },
    [load]
  );

  const updateBien = useCallback(
    (id: string, input: PlatformBienInput) => {
      const previous = biens.find((item) => item.id === id);
      setBiens((prev) => prev.map((item) => (item.id === id ? { ...item, ...input } : item)));

      const payload = {
        immeuble_id: input.immeubleId,
        type_bien: input.type,
        reference: nullable(input.reference),
        numero: nullable(input.numero),
        etage: nullable(input.etage),
        surface: input.surface || 0,
        loyer_mensuel: input.loyerMensuel || 0,
        statut: normalizeBienStatut(input.statut),
        description: nullable(input.description),
        infos_complementaires: nullable(input.infosComplementaires),
      };

      void (async () => {
        const { error: updateError } = await supabase.from("biens").update(payload).eq("id", id);
        if (updateError) {
          if (previous) setBiens((prev) => prev.map((item) => (item.id === id ? previous : item)));
          dispatchDataError({
            scope: "Biens",
            message: "Impossible de modifier le bien.",
            technical: describeSupabaseError(updateError),
          });
          return;
        }
        void load();
      })();
    },
    [biens, load]
  );

  const removeBien = useCallback(
    (id: string) => {
      const previous = biens.find((item) => item.id === id);
      setBiens((prev) => prev.filter((item) => item.id !== id));
      void (async () => {
        const { error: deleteError } = await supabase.from("biens").delete().eq("id", id);
        if (deleteError) {
          if (previous) setBiens((prev) => [previous, ...prev]);
          dispatchDataError({
            scope: "Biens",
            message: "Impossible de supprimer le bien.",
            technical: describeSupabaseError(deleteError),
          });
          return;
        }
        void load();
      })();
    },
    [biens, load]
  );

  /* ----------------------------- Locataires ----------------------------- */

  const addLocataire = useCallback(
    (input: PlatformLocataireInput) => {
      const optimistic: PlatformLocataire = { ...input, id: createUuid(), createdAt: new Date().toISOString() };
      setLocataires((prev) => [optimistic, ...prev]);

      const scopeIds = scopeRef.current;
      const isAgence = input.scope === "agence";
      const agenceId = isAgence ? scopeIds.agenceId : null;
      const proprietaireId = isAgence
        ? nullable(input.proprietaireId ?? "")
        : scopeIds.proprietaireId;

      const payload = {
        prenom: input.prenom,
        nom: input.nom,
        email: nullable(input.email),
        telephone: nullable(input.telephone),
        whatsapp: nullable(input.whatsapp),
        adresse: nullable(input.adresse),
        profession: nullable(input.profession),
        numero_piece: nullable(input.numeroPiece),
        type_piece: nullable(input.typePiece),
        notes: nullable(input.notes),
        contact_urgence: nullable(input.contactUrgence),
        proprietaire_id: proprietaireId,
        agence_id: agenceId,
      };

      void (async () => {
        const { data, error: insertError } = await supabase
          .from("locataires")
          .insert(payload)
          .select("id")
          .maybeSingle();
        if (insertError) {
          setLocataires((prev) => prev.filter((item) => item.id !== optimistic.id));
          dispatchDataError({
            scope: "Locataires",
            message: "Impossible d'enregistrer le locataire.",
            technical: describeSupabaseError(insertError),
          });
          return;
        }
        const realId = (data as Row | null)?.id;
        if (realId) {
          setLocataires((prev) =>
            prev.map((item) => (item.id === optimistic.id ? { ...item, id: String(realId) } : item))
          );
        }
        void load();
      })();

      return optimistic;
    },
    [load]
  );

  const updateLocataire = useCallback(
    (id: string, input: PlatformLocataireInput) => {
      const previous = locataires.find((item) => item.id === id);
      setLocataires((prev) => prev.map((item) => (item.id === id ? { ...item, ...input } : item)));

      const payload: Record<string, unknown> = {
        prenom: input.prenom,
        nom: input.nom,
        email: nullable(input.email),
        telephone: nullable(input.telephone),
        whatsapp: nullable(input.whatsapp),
        adresse: nullable(input.adresse),
        profession: nullable(input.profession),
        numero_piece: nullable(input.numeroPiece),
        type_piece: nullable(input.typePiece),
        notes: nullable(input.notes),
        contact_urgence: nullable(input.contactUrgence),
      };
      // Le rattachement (propriétaire/agence) ne change jamais via une édition.
      const rattachement = locataireRattachementRef.current.get(id);
      if (rattachement) {
        payload.proprietaire_id = rattachement.proprietaire_id;
        payload.agence_id = rattachement.agence_id;
      }

      void (async () => {
        const { error: updateError } = await supabase.from("locataires").update(payload).eq("id", id);
        if (updateError) {
          if (previous) setLocataires((prev) => prev.map((item) => (item.id === id ? previous : item)));
          dispatchDataError({
            scope: "Locataires",
            message: "Impossible de modifier le locataire.",
            technical: describeSupabaseError(updateError),
          });
          return;
        }
        void load();
      })();
    },
    [locataires, load]
  );

  const removeLocataire = useCallback(
    (id: string) => {
      const previous = locataires.find((item) => item.id === id);
      setLocataires((prev) => prev.filter((item) => item.id !== id));
      void (async () => {
        const { error: deleteError } = await supabase.from("locataires").delete().eq("id", id);
        if (deleteError) {
          if (previous) setLocataires((prev) => [previous, ...prev]);
          dispatchDataError({
            scope: "Locataires",
            message: "Impossible de supprimer le locataire (il est peut-être rattaché à un contrat).",
            technical: describeSupabaseError(deleteError),
          });
          return;
        }
        void load();
      })();
    },
    [locataires, load]
  );

  /* ------------------------------ Contrats ------------------------------ */
  // Contrats persistés dans la table `contrats` (Supabase). La portée est déduite
  // de l'immeuble du bien ; les enums frontend sont convertis vers la base.

  const contratPayload = (input: PlatformContratInput) => ({
    bien_id: input.bienId,
    locataire_id: input.locataireId,
    date_debut: input.dateDebut || null,
    date_fin: nullish(input.dateFin),
    loyer_mensuel: Math.max(0, Number(input.loyerMensuel) || 0),
    caution: Math.max(0, Number(input.caution) || 0),
    avance: Math.max(0, Number(input.avance) || 0),
    // Colonne `smallint` : arrondi strict à l'entier (1-31) pour éviter
    // toute erreur 22P02 si une décimale est saisie (ex. 1.09).
    jour_echeance: Math.min(31, Math.max(1, Math.round(Number(input.jourEcheance) || 1))),
    statut: contratStatutToDb(input.statut),
    conditions_particulieres: nullish(input.conditionsParticulieres),
    document: nullish(input.document),
  });

  const addContrat = useCallback(
    (input: PlatformContratInput) => {
      const optimistic: PlatformContrat = { ...input, id: createUuid(), createdAt: new Date().toISOString() };
      setContrats((prev) => [optimistic, ...prev]);

      void (async () => {
        const { data, error: insertError } = await supabase
          .from("contrats")
          .insert(contratPayload(input))
          .select("id")
          .maybeSingle();
        if (insertError) {
          setContrats((prev) => prev.filter((item) => item.id !== optimistic.id));
          dispatchDataError({
            scope: "Contrats",
            message: "Impossible d'enregistrer le contrat.",
            technical: describeSupabaseError(insertError),
          });
          return;
        }
        const realId = (data as Row | null)?.id;
        if (realId) {
          await genererLoyersManquants([{ ...optimistic, id: String(realId) }]);
        }
        void load();
      })();

      return optimistic;
    },
    [load]
  );

  const updateContrat = useCallback(
    (id: string, input: PlatformContratInput) => {
      const previous = contrats.find((item) => item.id === id);
      setContrats((prev) => prev.map((item) => (item.id === id ? { ...item, ...input } : item)));

      void (async () => {
        const { error: updateError } = await supabase.from("contrats").update(contratPayload(input)).eq("id", id);
        if (updateError) {
          if (previous) setContrats((prev) => prev.map((item) => (item.id === id ? previous : item)));
          dispatchDataError({
            scope: "Contrats",
            message: "Impossible de modifier le contrat.",
            technical: describeSupabaseError(updateError),
          });
          return;
        }
        void load();
      })();
    },
    [contrats, load]
  );

  const removeContrat = useCallback(
    (id: string) => {
      const previous = contrats.find((item) => item.id === id);
      setContrats((prev) => prev.filter((item) => item.id !== id));

      void (async () => {
        const { error: deleteError } = await supabase.from("contrats").delete().eq("id", id);
        if (deleteError) {
          if (previous) setContrats((prev) => [previous, ...prev]);
          dispatchDataError({
            scope: "Contrats",
            message: "Impossible de supprimer le contrat (des loyers y sont peut-être rattachés).",
            technical: describeSupabaseError(deleteError),
          });
          return;
        }
        void load();
      })();
    },
    [contrats, load]
  );

  /* ------------------------------ Paiements ----------------------------- */
  // Un paiement est rattaché à un loyer (table `paiements`). La RPC
  // `generer_loyer_contrat` garantit l'existence du loyer de la période ; le
  // trigger Supabase recalcule ensuite le statut du loyer automatiquement.

  const addPaiement = useCallback(
    (input: PlatformPaiementInput) => {
      const optimistic: PlatformPaiement = {
        id: createUuid(),
        scope: "proprietaire",
        contratId: input.contratId,
        proprietaireId: "",
        immeubleId: "",
        bienId: "",
        locataireId: "",
        periode: input.periode,
        montant: input.montant,
        date: input.date,
        mode: input.mode,
        reference: input.reference,
        note: input.note,
        createdAt: new Date().toISOString(),
      };

      void (async () => {
        try {
          const periodeFirst = monthFirstDay(input.periode);
          const { data: loyerId, error: genError } = await supabase.rpc("generer_loyer_contrat", {
            p_contrat_id: input.contratId,
            p_periode: periodeFirst,
          });
          if (genError) throw genError;

          let resolvedLoyerId = typeof loyerId === "string" ? loyerId : "";
          if (!resolvedLoyerId) {
            const { data: loyerRow, error: loyerError } = await supabase
              .from("loyers")
              .select("id")
              .eq("contrat_id", input.contratId)
              .eq("periode", periodeFirst)
              .maybeSingle();
            if (loyerError) throw loyerError;
            resolvedLoyerId = loyerRow ? String((loyerRow as Row).id) : "";
          }
          if (!resolvedLoyerId) throw new Error("Loyer introuvable pour cette période.");

          const { error: insertError } = await supabase.from("paiements").insert({
            loyer_id: resolvedLoyerId,
            montant: Math.max(0, Number(input.montant) || 0),
            date_paiement: input.date || todayISO(),
            mode_paiement: modePaiementToDb(input.mode),
            reference: nullish(input.reference),
            commentaire: nullish(input.note),
          });
          if (insertError) throw insertError;
          void load();
        } catch (caught) {
          dispatchDataError({
            scope: "Paiements",
            message: "Impossible d'enregistrer le paiement.",
            technical: describeSupabaseError(caught),
          });
        }
      })();

      return optimistic;
    },
    [load]
  );

  const removePaiement = useCallback(
    (id: string) => {
      const previous = paiements.find((item) => item.id === id);
      setPaiements((prev) => prev.filter((item) => item.id !== id));

      void (async () => {
        const { error: deleteError } = await supabase.from("paiements").delete().eq("id", id);
        if (deleteError) {
          if (previous) setPaiements((prev) => [previous, ...prev]);
          dispatchDataError({
            scope: "Paiements",
            message: "Impossible de supprimer le paiement.",
            technical: describeSupabaseError(deleteError),
          });
          return;
        }
        void load();
      })();
    },
    [paiements, load]
  );

  /* ------------------------------ Dépenses ------------------------------ */

  const depensePayload = (input: PlatformDepenseInput) => ({
    immeuble_id: nullish(input.immeubleId),
    bien_id: nullish(input.bienId),
    libelle: (input.libelle && input.libelle.trim()) || input.description.trim() || "Dépense",
    categorie: depenseCategorieToDb(input.categorie),
    montant: Math.max(0, Number(input.montant) || 0),
    date_depense: input.date || todayISO(),
    description: nullish(input.description),
    document: nullish(input.justificatif),
  });

  const addDepense = useCallback(
    (input: PlatformDepenseInput) => {
      const optimistic: PlatformDepense = {
        id: createUuid(),
        scope: "proprietaire",
        proprietaireId: "",
        immeubleId: input.immeubleId,
        bienId: input.bienId,
        libelle: input.libelle,
        categorie: input.categorie,
        montant: input.montant,
        date: input.date,
        description: input.description,
        justificatif: input.justificatif,
        createdAt: new Date().toISOString(),
      };
      setDepenses((prev) => [optimistic, ...prev]);

      void (async () => {
        const { error: insertError } = await supabase.from("depenses").insert(depensePayload(input));
        if (insertError) {
          setDepenses((prev) => prev.filter((item) => item.id !== optimistic.id));
          dispatchDataError({
            scope: "Dépenses",
            message: "Impossible d'enregistrer la dépense.",
            technical: describeSupabaseError(insertError),
          });
          return;
        }
        void load();
      })();

      return optimistic;
    },
    [load]
  );

  const updateDepense = useCallback(
    (id: string, input: PlatformDepenseInput) => {
      const previous = depenses.find((item) => item.id === id);
      setDepenses((prev) => prev.map((item) => (item.id === id ? { ...item, ...input } : item)));

      void (async () => {
        const { error: updateError } = await supabase.from("depenses").update(depensePayload(input)).eq("id", id);
        if (updateError) {
          if (previous) setDepenses((prev) => prev.map((item) => (item.id === id ? previous : item)));
          dispatchDataError({
            scope: "Dépenses",
            message: "Impossible de modifier la dépense.",
            technical: describeSupabaseError(updateError),
          });
          return;
        }
        void load();
      })();
    },
    [depenses, load]
  );

  const removeDepense = useCallback(
    (id: string) => {
      const previous = depenses.find((item) => item.id === id);
      setDepenses((prev) => prev.filter((item) => item.id !== id));

      void (async () => {
        const { error: deleteError } = await supabase.from("depenses").delete().eq("id", id);
        if (deleteError) {
          if (previous) setDepenses((prev) => [previous, ...prev]);
          dispatchDataError({
            scope: "Dépenses",
            message: "Impossible de supprimer la dépense.",
            technical: describeSupabaseError(deleteError),
          });
          return;
        }
        void load();
      })();
    },
    [depenses, load]
  );

  const syncLoyers = useCallback(async () => {
    await genererLoyersManquants(contrats);
    await load();
  }, [contrats, load]);

  /* ------------------------------ Sélecteurs ---------------------------- */

  const getImmeuble = useCallback((id: string) => immeubles.find((item) => item.id === id), [immeubles]);
  const getBien = useCallback((id: string) => biens.find((item) => item.id === id), [biens]);
  const getLocataire = useCallback((id: string) => locataires.find((item) => item.id === id), [locataires]);
  const getContrat = useCallback((id: string) => contrats.find((item) => item.id === id), [contrats]);

  const biensByImmeuble = useCallback(
    (immeubleId: string) => biens.filter((item) => item.immeubleId === immeubleId),
    [biens]
  );

  const contratsByImmeuble = useCallback(
    (immeubleId: string) => contrats.filter((item) => item.immeubleId === immeubleId),
    [contrats]
  );

  const contratsByBien = useCallback(
    (bienId: string) => contrats.filter((item) => item.bienId === bienId),
    [contrats]
  );

  const contratsByLocataire = useCallback(
    (locataireId: string) => contrats.filter((item) => item.locataireId === locataireId),
    [contrats]
  );

  const countContratsByBien = useCallback(
    (bienId: string) => contrats.filter((item) => item.bienId === bienId).length,
    [contrats]
  );

  const countContratsByLocataire = useCallback(
    (locataireId: string) => contrats.filter((item) => item.locataireId === locataireId).length,
    [contrats]
  );

  const contratActifByBien = useCallback(
    (bienId: string) => contrats.find((item) => item.bienId === bienId && item.statut === "actif"),
    [contrats]
  );

  const value = useMemo(
    () => ({
      immeubles,
      biens,
      locataires,
      contrats,
      loyers,
      impayes,
      paiements,
      depenses,
      loading,
      error,
      reload: load,
      addImmeuble,
      updateImmeuble,
      removeImmeuble,
      addBien,
      updateBien,
      removeBien,
      addLocataire,
      updateLocataire,
      removeLocataire,
      addContrat,
      updateContrat,
      removeContrat,
      addPaiement,
      removePaiement,
      addDepense,
      updateDepense,
      removeDepense,
      syncLoyers,
      getImmeuble,
      getBien,
      getLocataire,
      getContrat,
      biensByImmeuble,
      contratsByImmeuble,
      contratsByBien,
      contratsByLocataire,
      countContratsByBien,
      countContratsByLocataire,
      contratActifByBien,
    }),
    [
      immeubles,
      biens,
      locataires,
      contrats,
      loyers,
      impayes,
      paiements,
      depenses,
      loading,
      error,
      load,
      addImmeuble,
      updateImmeuble,
      removeImmeuble,
      addBien,
      updateBien,
      removeBien,
      addLocataire,
      updateLocataire,
      removeLocataire,
      addContrat,
      updateContrat,
      removeContrat,
      addPaiement,
      removePaiement,
      addDepense,
      updateDepense,
      removeDepense,
      syncLoyers,
      getImmeuble,
      getBien,
      getLocataire,
      getContrat,
      biensByImmeuble,
      contratsByImmeuble,
      contratsByBien,
      contratsByLocataire,
      countContratsByBien,
      countContratsByLocataire,
      contratActifByBien,
    ]
  );

  return <PlatformDataContext.Provider value={value}>{children}</PlatformDataContext.Provider>;
}

export function usePlatformData(): PlatformDataContextValue {
  const context = useContext(PlatformDataContext);
  if (!context) {
    throw new Error("usePlatformData doit être utilisé à l'intérieur de PlatformDataProvider");
  }
  return context;
}